import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Card, Row, Col, Button, Spinner } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { BASE_URL, paybridge_Url } from "../../Config";
import {
  isRsaSaleContext,
  normalizeRetailAppDashboard,
} from "../../utils/rsaSale";
import AppLoader from "../AppLoader";
import "../../Pages/Company/Company.css";

const todayIsoDate = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Default = current day only (FromDate + ToDate = today). No ServiceId. */
const getDefaultRsaFilters = () => {
  const today = todayIsoDate();
  return {
    AgentId: "",
    FromDate: today,
    ToDate: today,
    Month: "",
    Year: "",
    MobileNumber: "",
    Status: "",
    TypeOfPayment: "",
  };
};

/** Full reset — all filters empty (including current-day dates). */
const getEmptyRsaFilters = () => ({
  AgentId: "",
  FromDate: "",
  ToDate: "",
  Month: "",
  Year: "",
  MobileNumber: "",
  Status: "",
  TypeOfPayment: "",
});

const nullIfEmpty = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
};

const buildRetailAppDashboardBody = (companyId, filters = {}) => ({
  CompanyId: companyId || null,
  AgentId: nullIfEmpty(filters.AgentId),
  FromDate: nullIfEmpty(filters.FromDate),
  ToDate: nullIfEmpty(filters.ToDate),
  Month: nullIfEmpty(filters.Month) ? Number(filters.Month) : null,
  Year: nullIfEmpty(filters.Year) ? Number(filters.Year) : null,
  MobileNumber: nullIfEmpty(filters.MobileNumber),
  Status: nullIfEmpty(filters.Status),
  TypeOfPayment: nullIfEmpty(filters.TypeOfPayment),
  ServiceId: null,
});

const AllRecord = () => {
  const [data, setData] = useState({
    userId: "",
    name: "",
  });

  const [summaryData, setSummaryData] = useState({
    total_payments: 0,
    completed_count: 0,
    pending_count: 0,
    failed_count: 0,
    todaySales: 0,
    monthSales: 0,
    totalSales: 0,
    todayRevenueLabel: "0",
    monthRevenueLabel: "0",
    totalRevenueLabel: "0",
    totalEarned: 0,
    totalEarnedLabel: "0",
    agentWise: [],
  });

  const [profileId, setProfileId] = useState("");
  const [isRsaSaleCompany, setIsRsaSaleCompany] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState(getDefaultRsaFilters);

  const fetchDashboardData = async (companyProfileId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Dashboard?CompanyId=${companyProfileId}`
      );
      if (response.data) {
        setData({
          userId: response.data.userId,
          name: response.data.companyName || "Company",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch dashboard data.", "error");
    }
  };

  const fetchAgents = async (companyProfileId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Agents?userId=${companyProfileId}`,
        { headers: { accept: "*/*" } }
      );
      if (
        response.data?.responseCode === "0000" &&
        Array.isArray(response.data.agents)
      ) {
        setAgents(response.data.agents);
      } else {
        setAgents([]);
      }
    } catch {
      setAgents([]);
    }
  };

  const fetchSummaryData = useCallback(
    async (companyProfileId, rsaSale = isRsaSaleCompany, filterValues = filters) => {
      if (!companyProfileId) return;
      try {
        // RSASALE company only — RetailApp_Dashboard (full filter body)
        if (rsaSale) {
          const body = buildRetailAppDashboardBody(companyProfileId, filterValues);
          const response = await axios.post(
            `${BASE_URL}RetailApp_Dashboard`,
            body,
            { headers: { "Content-Type": "application/json" } }
          );
          setSummaryData(normalizeRetailAppDashboard(response.data));
          const companyLabel =
            response.data?.companyName ||
            response.data?.data?.companyName ||
            response.data?.CompanyName;
          if (companyLabel) {
            setData((prev) => ({ ...prev, name: companyLabel }));
          }
          return;
        }

        // Existing paybridge summary for all other companies
        const payload = {
          companyId: companyProfileId,
          type: "all",
        };

        const response = await axios.post(
          `${paybridge_Url}report/company/summary`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.success) {
          setSummaryData(response.data.data);
        } else {
          Swal.fire("Error", "No summary data returned from API", "error");
        }
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Failed to fetch summary data.", "error");
      }
    },
    [filters, isRsaSaleCompany]
  );

  useEffect(() => {
    const storedUserData = sessionStorage.getItem("user");
    if (!storedUserData) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      setLoading(false);
      return;
    }

    const userData = JSON.parse(storedUserData);
    const companyProfileId =
      userData.userRole === "Manager" ||
      userData.userRole === "Admin" ||
      userData.userRole === "Finance"
        ? userData.selectedCompanyId || userData.companyId || userData.companyProfileId
        : userData.userId || userData.companyProfileId || userData.companyId;
    const companyName =
      userData.selectedCompanyName || userData.companyName || userData.name || "Company";
    const rsaSale = isRsaSaleContext({
      productSale: userData.selectedProductSale || userData.productSale,
      companyName,
      name: companyName,
    });

    setData((prev) => ({ ...prev, name: companyName }));
    setProfileId(companyProfileId);
    setIsRsaSaleCompany(rsaSale);

    if (companyProfileId) {
      const defaultFilters = getDefaultRsaFilters();
      if (rsaSale) setFilters(defaultFilters);

      const tasks = rsaSale
        ? [
            fetchSummaryData(companyProfileId, true, defaultFilters),
            fetchAgents(companyProfileId),
          ]
        : [
            fetchDashboardData(companyProfileId),
            fetchSummaryData(companyProfileId, false, defaultFilters),
          ];
      Promise.all(tasks).finally(() => setLoading(false));
    } else {
      Swal.fire(
        "Error",
        "No company is selected. Please open a company from the Manager Dashboard.",
        "error"
      );
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = async () => {
    if (!profileId) return;
    setRefreshing(true);
    await fetchSummaryData(profileId, isRsaSaleCompany, filters);
    setRefreshing(false);
  };

  const handleClearFilters = async () => {
    const defaults = getDefaultRsaFilters();
    setFilters(defaults);
    if (!profileId) return;
    setRefreshing(true);
    await fetchSummaryData(profileId, isRsaSaleCompany, defaults);
    setRefreshing(false);
  };

  const handleResetFilters = () => {
    // Only clear filter inputs — keep current dashboard data; API on Apply only
    setFilters(getEmptyRsaFilters());
  };

  const handleRefresh = async () => {
    if (!profileId) return;
    setRefreshing(true);
    await fetchSummaryData(profileId, isRsaSaleCompany, filters);
    setRefreshing(false);
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <Card className="co-card">
        <Card.Body>
          <AppLoader
            rsa={isRsaSaleCompany}
            message={
              isRsaSaleCompany
                ? "Loading RSA dashboard..."
                : "Loading summary..."
            }
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={`co-card${refreshing ? " co-card-loading" : ""}`}>
      {refreshing && (
        <AppLoader
          rsa={isRsaSaleCompany}
          overlay
          compact
          message={
            isRsaSaleCompany ? "Updating RSA dashboard..." : "Refreshing..."
          }
        />
      )}
      <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
        <div>
          <h5 className="mb-0">
            <i
              className={`bi ${isRsaSaleCompany ? "bi-shield-check" : "bi-calendar3"} me-2 ${
                isRsaSaleCompany ? "" : "text-primary"
              }`}
              style={isRsaSaleCompany ? { color: "#f58123" } : undefined}
            />
            {data.name || "Company"} —{" "}
            {isRsaSaleCompany ? "RSA Overview" : "All Time"}
          </h5>
          <small className="text-muted">
            {isRsaSaleCompany
              ? "Filter RSA dashboard by date, agent, payment and status"
              : "Lifetime payment summary"}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isRsaSaleCompany && <span className="co-rsa-chip">RSASALE</span>}
          <Button
            variant={isRsaSaleCompany ? "outline-success" : "outline-primary"}
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <i className="bi bi-arrow-clockwise me-1" />
            )}
            Refresh
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        {isRsaSaleCompany ? (
          <>
            <div className="co-filter-bar mb-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.FromDate}
                    onChange={(e) => handleFilterChange("FromDate", e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.ToDate}
                    onChange={(e) => handleFilterChange("ToDate", e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Month</label>
                  <select
                    className="form-select"
                    value={filters.Month}
                    onChange={(e) => handleFilterChange("Month", e.target.value)}
                  >
                    <option value="">All</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={String(i + 1)}>
                        {new Date(2000, i, 1).toLocaleString("en", { month: "short" })}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Year</label>
                  <select
                    className="form-select"
                    value={filters.Year}
                    onChange={(e) => handleFilterChange("Year", e.target.value)}
                  >
                    <option value="">All</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Agent</label>
                  <select
                    className="form-select"
                    value={filters.AgentId}
                    onChange={(e) => handleFilterChange("AgentId", e.target.value)}
                  >
                    <option value="">All Agents</option>
                    {agents.map((a) => (
                      <option key={a.agentId || a.userId} value={a.agentId || a.userId}>
                        {a.name || a.agentName || a.email || "Agent"}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Mobile</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="03xx..."
                    value={filters.MobileNumber}
                    onChange={(e) => handleFilterChange("MobileNumber", e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Status</label>
                  <select
                    className="form-select"
                    value={filters.Status}
                    onChange={(e) => handleFilterChange("Status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Success">Success</option>
                    <option value="Pending">Pending</option>
                    <option value="Failed">Failed</option>
                  </select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Payment</label>
                  <select
                    className="form-select"
                    value={filters.TypeOfPayment}
                    onChange={(e) => handleFilterChange("TypeOfPayment", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="Cash">Cash</option>
                    <option value="Wallet Payment">Wallet Payment</option>
                    <option value="Card">Card</option>
                  </select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={handleApplyFilters}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <i className="bi bi-search me-1" />
                        Apply
                      </>
                    )}
                  </Button>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={handleClearFilters}
                    disabled={refreshing}
                  >
                    Today
                  </Button>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button
                    variant="outline-danger"
                    className="w-100"
                    onClick={handleResetFilters}
                    disabled={refreshing}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            <Row className="g-3">
              <InfoCard
                title="Today Sales"
                value={summaryData.todaySales}
                subtitle={`Revenue PKR ${summaryData.todayRevenueLabel}`}
                icon="bi-calendar-day"
                variant="pending"
              />
              <InfoCard
                title="Month Sales"
                value={summaryData.monthSales}
                subtitle={`Revenue PKR ${summaryData.monthRevenueLabel}`}
                icon="bi-calendar3"
                variant="ok"
              />
              <InfoCard
                title="Total Sales"
                value={summaryData.totalSales}
                subtitle={`Revenue PKR ${summaryData.totalRevenueLabel}`}
                icon="bi-shield-plus"
                variant="total"
              />
              {Number(summaryData.totalEarned) > 0 && (
                <InfoCard
                  title="Total Earned"
                  value={`PKR ${summaryData.totalEarnedLabel}`}
                  subtitle="Commission / earned"
                  icon="bi-cash-stack"
                  variant="ok"
                />
              )}
            </Row>

            {Array.isArray(summaryData.agentWise) &&
              summaryData.agentWise.length > 0 && (
                <div className="mt-4">
                  <h6 className="fw-semibold mb-3">
                    <i className="bi bi-people me-2" style={{ color: "#f58123" }} />
                    Agent-wise Sales
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Agent</th>
                          <th className="text-end">Sales</th>
                          <th className="text-end">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.agentWise.slice(0, 25).map((agent, idx) => (
                          <tr key={agent.agentId || idx}>
                            <td>{idx + 1}</td>
                            <td>{agent.agentName || "-"}</td>
                            <td className="text-end">
                              {Number(agent.totalSales || 0).toLocaleString()}
                            </td>
                            <td className="text-end">
                              PKR{" "}
                              {Number(agent.totalRevenue || 0).toLocaleString(
                                "en-PK",
                                { maximumFractionDigits: 0 }
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {summaryData.agentWise.length > 25 && (
                    <small className="text-muted d-block mt-2">
                      Showing top 25 of {summaryData.agentWise.length} agents
                    </small>
                  )}
                </div>
              )}
          </>
        ) : (
          <Row className="g-3">
            <InfoCard
              title="Total Payments"
              value={summaryData.total_payments}
              icon="bi-cash-stack"
              variant="total"
            />
            <InfoCard
              title="Completed"
              value={summaryData.completed_count}
              icon="bi-check-circle"
              variant="ok"
            />
            <InfoCard
              title="Pending"
              value={summaryData.pending_count}
              icon="bi-hourglass-split"
              variant="pending"
            />
            <InfoCard
              title="Failed"
              value={summaryData.failed_count}
              icon="bi-x-circle"
              variant="fail"
            />
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

const InfoCard = ({ title, value, icon, variant, subtitle }) => (
  <Col xs={12} sm={6} lg={3}>
    <div className={`co-stat co-stat-${variant} p-3`}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="text-muted small fw-semibold">{title}</span>
        <span className="co-stat-icon">
          <i className={`bi ${icon}`} />
        </span>
      </div>
      <div className="fs-3 fw-bold text-dark">{value ?? 0}</div>
      {subtitle ? <small className="text-muted">{subtitle}</small> : null}
    </div>
  </Col>
);

export default AllRecord;
